import { useCallback, useEffect, useRef, useState } from "react";

interface UseStreamingBufferedContentOptions {
  content: string;
  batchSize?: number; // Flush immediately if this many chars added since last flush
  flushDelay?: number; // Max time to wait before flushing
}

const DEFAULT_BATCH_SIZE = 20;
const DEFAULT_FLUSH_DELAY = 80;

/**
 * Hook for buffering streaming content to reduce frequency of updates.
 * Takes in raw streaming content and returns smoothly buffered content.
 *
 * @param content - The raw streaming content to buffer
 * @param batchSize - Flush immediately if this many chars added since last flush (default: 20)
 * @param flushDelay - Max time to wait before flushing in milliseconds (default: 80)
 * @returns Buffered content string
 */
export const useStreamingBufferedContent = ({
  content,
  batchSize = DEFAULT_BATCH_SIZE,
  flushDelay = DEFAULT_FLUSH_DELAY,
}: UseStreamingBufferedContentOptions): string => {
  const [bufferedContent, setBufferedContent] = useState<string>(content);
  const flushTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastFlushedLengthRef = useRef(content.length); // Initialize with initial content length
  const contentRef = useRef(content);

  // Always keep contentRef current
  contentRef.current = content;

  const flush = useCallback(() => {
    setBufferedContent(contentRef.current);
    lastFlushedLengthRef.current = contentRef.current.length;

    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }
  }, []); // No dependencies - always uses current content via ref

  const scheduleFlush = useCallback(() => {
    // Only schedule if no flush is already pending
    if (!flushTimeoutRef.current) {
      flushTimeoutRef.current = setTimeout(flush, flushDelay);
    }
  }, [flush, flushDelay]);

  useEffect(() => {
    // No content, reset everything
    if (!content) {
      setBufferedContent("");
      lastFlushedLengthRef.current = 0;
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
        flushTimeoutRef.current = null;
      }
      return;
    }

    const newLength = content.length;
    const lastLength = lastFlushedLengthRef.current;

    if (newLength < lastLength) {
      // Content shrunk - flush immediately (likely reset/truncation)
      flush();
    } else if (newLength === lastLength && content !== bufferedContent) {
      // Same length but different content - replacement, flush immediately
      flush();
    } else if (newLength > lastLength) {
      // Content grew - use normal batching logic for performance
      const charsDiffSinceFlush = newLength - lastLength;
      if (charsDiffSinceFlush >= batchSize) {
        flush();
      } else {
        scheduleFlush();
      }
    }
    // If newLength === lastLength && content === bufferedContent, no change - do nothing
  }, [content, batchSize, flush, scheduleFlush, bufferedContent]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }
    };
  }, []);

  return bufferedContent;
};
